import * as React from 'react';
import { Modal, Button, Alert, Space, Typography } from 'antd';
import { ListLayout, ListLayoutItem } from 'app/shared/ui/ListLayout';
import type { BreakdownVM } from '../../domain/packageBreakdowns/types';
import { formatPercent, buildDonutSegments } from '../../domain/packageBreakdowns/helpers';
import { DonutChart } from './DonutChart';
import { BreakdownLegendLine } from './BreakdownLegendLine';

const { Text } = Typography;

export interface BreakdownsModalProps {
  open: boolean;
  title: string;
  items: BreakdownVM[];
  colors: Map<string, string>;
  total: number;
  hoverKey: string | null;
  onHoverKeyChange: (key: string | null) => void;
  tooltipByKey: Map<string, string>;
  onClose: () => void;
}

export const BreakdownsModal: React.FC<BreakdownsModalProps> = ({
  open,
  title,
  items,
  colors,
  total,
  hoverKey,
  onHoverKeyChange,
  tooltipByKey,
  onClose,
}) => {
  const content = React.useMemo(() => {
    if (!items.length) return <Text type="secondary">—</Text>;

    const badTotal = Math.abs(total - 100) > 0.5;
    const { segs } = buildDonutSegments(items, colors);

    return (
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        {badTotal ? (
          <Alert
            type="warning"
            showIcon
            title={`Total is ${formatPercent(total)} (expected 100%)`}
          />
        ) : null}

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <DonutChart
            segs={segs}
            hoverKey={hoverKey}
            onHover={onHoverKeyChange}
            tooltipByKey={tooltipByKey}
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <ListLayout size="small">
              {items.map((x) => (
                <ListLayoutItem
                  key={x.key}
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                  onMouseEnter={() => onHoverKeyChange(x.key)}
                  onMouseLeave={() => onHoverKeyChange(null)}
                >
                  <BreakdownLegendLine
                    x={x}
                    color={colors.get(x.key) ?? 'rgba(0,0,0,0.25)'}
                    truncate={false}
                    highlighted={hoverKey === x.key}
                  />
                </ListLayoutItem>
              ))}
            </ListLayout>
          </div>
        </div>
      </Space>
    );
  }, [items, colors, total, hoverKey, onHoverKeyChange, tooltipByKey]);

  return (
    <Modal
      centered
      open={open}
      title={title}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={760}
    >
      {content}
    </Modal>
  );
};

